import { Component, NgZone, OnDestroy, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription, forkJoin, map } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';

import { CelulasService } from '../../core/services/celulas.service';
import { IglesiasService } from '../../core/services/iglesias.service';
import { CelulaMapa } from '../../shared/interfaces/celula.interface';
import { MapaCelulasComponent } from '../../shared/components/mapa-celulas/mapa-celulas.component';
import { SkeletonBlockComponent } from '../../shared/components/skeleton-block/skeleton-block.component';
import { UserStore } from '../../core/services/user-store.service';
import { UsersService } from '../../core/services/users.service';
import {
  PieChartData,
  ReunionCounts,
  buildBautismoPie,
  buildReunionPie,
  buildSexoPie,
} from '../../shared/helpers/grafico-pie';
import { BarChartData, buildEdadesBar, buildNivelesBar } from '../../shared/helpers/grafico-barra';
import { GraficoBarraComponent } from '../../shared/components/grafico-barra/grafico-barra.component';
import {
  SerieChartData,
  addDaysIso,
  buildCelulaSerie,
  buildDomingoSerie,
  fechasSemana,
  formatDisplay,
  formatIso,
} from '../../shared/helpers/grafico-serie';
import { ultimoDomingo } from '../../shared/helpers/perfil-asistencia';
import { GraficoSerieComponent } from '../../shared/components/grafico-serie/grafico-serie.component';
import { GraficoTortaComponent } from '../../shared/components/grafico-torta/grafico-torta.component';
import { IglesiaProfile } from '../../shared/interfaces/iglesia.interface';
import { OrganigramaComponent } from './organigrama/organigrama.component';

/**
 * SCRUM-93: layout de gráficos, alineado a mayorap2.
 * Los datos de cada gráfico entran en las historias siguientes.
 */
@Component({
  selector: 'app-graficos',
  standalone: true,
  imports: [
    Button,
    Card,
    GraficoBarraComponent,
    GraficoSerieComponent,
    GraficoTortaComponent,
    MapaCelulasComponent,
    OrganigramaComponent,
    SkeletonBlockComponent,
  ],
  templateUrl: './graficos.component.html',
  styleUrl: './graficos.component.scss',
})
export class GraficosComponent implements OnDestroy {
  private readonly iglesiasService = inject(IglesiasService);
  private readonly usersService = inject(UsersService);
  private readonly userStore = inject(UserStore);
  private readonly route = inject(ActivatedRoute, { optional: true });
  private readonly celulasService = inject(CelulasService);
  private readonly messages = inject(MessageService);
  private readonly zone = inject(NgZone);
  private readonly reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private conteoFrame = 0;
  private readonly iglesiaSub?: Subscription;
  private mapaSub?: Subscription;
  private piesSub?: Subscription;
  private domingoSub?: Subscription;
  private celulaSub?: Subscription;
  private resumenSub?: Subscription;

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly iglesia = signal<IglesiaProfile | null>(null);
  /** Nombre pedido por el super panel. Vacío si se usan los gráficos de la sesión. */
  readonly iglesiaQuery = signal<string | null>(null);

  readonly suspendido = computed(() => {
    const iglesia = this.iglesia();
    if (!iglesia) {
      return false;
    }
    return iglesia.suspender || !iglesia.svIglesia;
  });

  readonly showNiveles = computed(() => (this.iglesia()?.setLevel ?? 0) !== 0);
  readonly showReunion = computed(() => (this.iglesia()?.cantCulto ?? 1) !== 1);
  readonly showMapa = computed(() => this.userStore.isAdmin() || !!this.iglesiaQuery());
  readonly sexoPie = signal<PieChartData | null>(null);
  readonly bautismoPie = signal<PieChartData | null>(null);
  readonly piesError = signal<string | null>(null);
  readonly domingoSerie = signal<SerieChartData | null>(null);
  readonly celulaSerie = signal<SerieChartData | null>(null);
  readonly domingoError = signal<string | null>(null);
  readonly celulaError = signal<string | null>(null);
  readonly domingoFecha = signal(formatIso(ultimoDomingo()));
  readonly celulaFecha = signal(formatIso(ultimoDomingo()));
  readonly domingoAtras = signal(0);
  readonly celulaAtras = signal(0);
  readonly domingoVisible = computed(() => formatDisplay(this.domingoFecha()));
  readonly celulaVisible = computed(() => formatDisplay(this.celulaFecha()));
  readonly domingoAtraso = computed(() => atrasoTexto(this.domingoAtras()));
  readonly celulaAtraso = computed(() => atrasoTexto(this.celulaAtras()));
  readonly cantAdmin = signal<number | null>(null);
  readonly cantLideres = signal<number | null>(null);
  readonly enCelula = signal<number | null>(null);
  readonly sinCelula = signal<number | null>(null);
  readonly cantAdminVista = signal<number | null>(null);
  readonly cantLideresVista = signal<number | null>(null);
  readonly enCelulaVista = signal<number | null>(null);
  readonly sinCelulaVista = signal<number | null>(null);
  readonly edadesBar = signal<BarChartData | null>(null);
  readonly nivelesBar = signal<BarChartData | null>(null);
  readonly reunionPie = signal<PieChartData | null>(null);
  readonly distribucionError = signal<string | null>(null);
  readonly mapaCelulas = signal<CelulaMapa[] | null>(null);
  readonly mapaLoading = signal(false);
  readonly mapaError = signal<string | null>(null);
  readonly vista = signal<'resumen' | 'organigrama'>('resumen');

  constructor() {
    const pedida = this.route?.snapshot.queryParamMap.get('iglesia')?.trim() ?? '';
    if (this.userStore.isSuper() && pedida) {
      this.iglesiaQuery.set(pedida);
    }

    const carga: Observable<IglesiaProfile> = this.iglesiaQuery()
      ? this.iglesiasService.listTodas().pipe(
          map((lista) => {
            const encontrada = lista.find((item) => item.nombreIglesia === pedida);
            if (!encontrada) {
              throw new Error('No se encontró la iglesia');
            }
            return encontrada;
          }),
        )
      : this.iglesiasService.getCurrentIglesia();

    this.iglesiaSub = carga.subscribe({
      next: (iglesia) => {
        this.iglesia.set(iglesia);
        this.loading.set(false);
        if (iglesia.svIglesia && !iglesia.suspender) {
          this.loadPies();
          this.loadDomingo();
          this.loadCelula();
          this.loadDistribucion();
        }
      },
      error: () => {
        this.loadError.set('No se pudo cargar la iglesia.');
        this.loading.set(false);
      },
    });
  }

  ngOnDestroy(): void {
    if (this.conteoFrame) {
      cancelAnimationFrame(this.conteoFrame);
      this.conteoFrame = 0;
    }
    this.iglesiaSub?.unsubscribe();
    this.piesSub?.unsubscribe();
    this.domingoSub?.unsubscribe();
    this.celulaSub?.unsubscribe();
    this.resumenSub?.unsubscribe();
    this.mapaSub?.unsubscribe();
  }

  verResumen(): void {
    this.vista.set('resumen');
  }

  verOrganigrama(): void {
    this.vista.set('organigrama');
  }

  verMapa(): void {
    if (!this.showMapa()) {
      this.accesoDenegado();
      return;
    }
    this.mapaLoading.set(true);
    this.mapaError.set(null);
    this.mapaSub?.unsubscribe();
    this.mapaSub = this.celulasService.getMapa(this.iglesiaQuery() ?? undefined).subscribe({
      next: (response) => {
        this.mapaCelulas.set(response.data ?? []);
        this.mapaLoading.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.mapaLoading.set(false);
        if (error.status === 403) {
          this.accesoDenegado();
          return;
        }
        this.mapaError.set('No se pudo cargar el mapa de células.');
      },
    });
  }

  cerrarMapa(): void {
    this.mapaCelulas.set(null);
  }

  private accesoDenegado(): void {
    this.messages.add({
      severity: 'error',
      summary: 'Acceso denegado',
      detail: 'Solo los administradores pueden ver el mapa de células.',
    });
  }

  domingoAnterior(): void {
    this.mover('domingo', -1);
  }

  domingoSiguiente(): void {
    this.mover('domingo', 1);
  }

  celulaAnterior(): void {
    this.mover('celula', -1);
  }

  celulaSiguiente(): void {
    this.mover('celula', 1);
  }

  private mover(chart: 'domingo' | 'celula', direction: -1 | 1): void {
    const atras = chart === 'domingo' ? this.domingoAtras : this.celulaAtras;
    const fecha = chart === 'domingo' ? this.domingoFecha : this.celulaFecha;
    if (direction > 0 && atras() <= 0) {
      return;
    }
    fecha.set(addDaysIso(fecha(), direction * 7));
    atras.update((value) => value - direction);
    if (chart === 'domingo') {
      this.loadDomingo();
      return;
    }
    this.loadCelula();
  }

  private nombreIglesia(): string {
    return this.iglesiaQuery() ?? this.userStore.iglesia();
  }

  private loadPies(): void {
    const iglesia = this.nombreIglesia();
    if (!iglesia) {
      this.piesError.set('No hay iglesia en sesión.');
      return;
    }

    this.piesSub = forkJoin({
      mujeres: this.usersService.getCantMujeres(iglesia),
      hombres: this.usersService.getCantHombres(iglesia),
      bautismo: this.usersService.getBautismo(iglesia),
    }).subscribe({
      next: ({ mujeres, hombres, bautismo }) => {
        this.sexoPie.set(buildSexoPie(hombres.totalHombres ?? 0, mujeres.totalMujeres ?? 0));
        this.bautismoPie.set(buildBautismoPie(bautismo?.[0] ?? 0, bautismo?.[1] ?? 0));
      },
      error: () => this.piesError.set('No se pudieron cargar los gráficos de sexo y bautismo.'),
    });
  }

  private loadDomingo(): void {
    const iglesia = this.nombreIglesia();
    if (!iglesia) {
      this.domingoError.set('No hay iglesia en sesión.');
      return;
    }
    const fechas = fechasSemana(this.domingoFecha());
    const labels = [...fechas].reverse().map(formatDisplay);
    this.domingoError.set(null);
    this.domingoSub?.unsubscribe();
    this.domingoSub = forkJoin({
      domingo: this.usersService.getConteoDom(fechas, iglesia),
      full: this.usersService.getConteoFull(fechas, iglesia),
    }).subscribe({
      next: ({ domingo, full }) => {
        const enCelula = fechas.map((_, index) => domingo[`totalDom${index}`] ?? 0);
        const sinCelula = fechas.map((_, index) =>
          Math.max(0, (full[index]?.contador ?? 0) - enCelula[index]),
        );
        this.domingoSerie.set(buildDomingoSerie([...enCelula].reverse(), [...sinCelula].reverse(), labels));
      },
      error: () => this.domingoError.set('No se pudo cargar la asistencia de domingo.'),
    });
  }

  private loadCelula(): void {
    const iglesia = this.nombreIglesia();
    if (!iglesia) {
      this.celulaError.set('No hay iglesia en sesión.');
      return;
    }
    const fechas = fechasSemana(this.celulaFecha());
    const labels = [...fechas].reverse().map(formatDisplay);
    this.celulaError.set(null);
    this.celulaSub?.unsubscribe();
    this.celulaSub = forkJoin({
      presentes: this.usersService.getConteoCelTrue(fechas, iglesia),
      ausentes: this.usersService.getConteoCelFalse(fechas, iglesia),
    }).subscribe({
      next: ({ presentes, ausentes }) => {
        const celPresentes = fechas.map((_, index) => presentes[`totalCelTrue${index}`] ?? 0);
        const celAusentes = fechas.map((_, index) => ausentes[`totalCelFalse${index}`] ?? 0);
        this.celulaSerie.set(buildCelulaSerie([...celPresentes].reverse(), [...celAusentes].reverse(), labels));
      },
      error: () => this.celulaError.set('No se pudo cargar la asistencia de célula.'),
    });
  }

  private loadDistribucion(): void {
    const iglesia = this.nombreIglesia();
    if (!iglesia) {
      this.distribucionError.set('No hay iglesia en sesión.');
      return;
    }
    const setLevel = this.iglesia()?.setLevel ?? 0;
    const cantCulto = this.iglesia()?.cantCulto ?? 1;
    this.resumenSub?.unsubscribe();
    this.resumenSub = forkJoin({
      admin: this.usersService.getCantAdmin(iglesia),
      lideres: this.usersService.getCantLideres(iglesia),
      encelu: this.usersService.getEnCelula(iglesia),
      sincelu: this.usersService.getSinCelula(iglesia),
      edades: this.usersService.getEdades(iglesia),
      niveles: this.usersService.getCantNiveles(iglesia),
      reunion: this.usersService.getReunionSelect(iglesia),
    }).subscribe({
      next: ({ admin, lideres, encelu, sincelu, edades, niveles, reunion }) => {
        this.cantAdmin.set(admin.cantAdmin ?? 0);
        this.cantLideres.set(lideres.cantLideres ?? 0);
        this.enCelula.set(encelu.encelu ?? 0);
        this.sinCelula.set(sincelu.sincelu ?? 0);
        this.animarConteos();
        this.edadesBar.set(buildEdadesBar(edades));
        this.nivelesBar.set(
          buildNivelesBar(
            [0, 1, 2, 3, 4, 5].map((nivel) => niveles[`getNv${nivel}`] ?? 0),
            setLevel,
          ),
        );
        this.reunionPie.set(buildReunionPie(reunion as ReunionCounts, cantCulto));
      },
      error: () => this.distribucionError.set('No se pudieron cargar edades, niveles y reuniones.'),
    });
  }

  private animarConteos(): void {
    if (this.conteoFrame) {
      cancelAnimationFrame(this.conteoFrame);
      this.conteoFrame = 0;
    }
    const metas = {
      admin: this.cantAdmin() ?? 0,
      lideres: this.cantLideres() ?? 0,
      enCelula: this.enCelula() ?? 0,
      sinCelula: this.sinCelula() ?? 0,
    };
    const aplicar = (factor: number) => {
      this.cantAdminVista.set(Math.round(metas.admin * factor));
      this.cantLideresVista.set(Math.round(metas.lideres * factor));
      this.enCelulaVista.set(Math.round(metas.enCelula * factor));
      this.sinCelulaVista.set(Math.round(metas.sinCelula * factor));
    };
    if (this.reduceMotion) {
      aplicar(1);
      return;
    }
    aplicar(0);
    const inicio = performance.now();
    const tick = (ahora: number) => {
      const avance = Math.min(1, (ahora - inicio) / DURACION_CONTEO);
      const curva = 1 - (1 - avance) ** 3;
      this.zone.run(() => aplicar(avance < 1 ? curva : 1));
      if (avance < 1) {
        this.conteoFrame = requestAnimationFrame(tick);
        return;
      }
      this.conteoFrame = 0;
    };
    this.zone.runOutsideAngular(() => {
      this.conteoFrame = requestAnimationFrame(tick);
    });
  }
}

const DURACION_CONTEO = 2500;

function atrasoTexto(semanas: number): string | null {
  if (semanas <= 0) {
    return null;
  }
  return semanas === 1
    ? 'El gráfico está atrasado 1 semana'
    : `El gráfico está atrasado ${semanas} semanas`;
}
